import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import type {
    Json,
    ICustomPageLayoutDTO,
    ICustomPageTypeItemsSourceDTO,
    ICustomPageTypeDefaultValueDTO,
    ICustomPageLayoutMultiReportDTO,
    ICustomPageLayoutHeader,
} from '@se/cp.common';
import { CacheService, ServerRootService } from '@se/se.ui.html5';
import { Observable, map } from 'rxjs';

import {
    ClinicalRepositoryData,
    IDatasetResponseDTO,
    ImportReportListRequest,
    IPatientHistory,
    PatientHistoryRequest,
} from './http-models';
import { XmlRequestType } from '../../services/imaging/common';
import { ecgHistorySearchParam } from '@se/models';

export enum EMeasureDataType {
    Sr,
    Xml,
}

interface BaseImportRequest {
    report: Json;
    admissionDate: string;
    studyDate: string;
    studyInstanceUID: string;
}

export interface MeasureImportRequest extends BaseImportRequest {
    idRepository: string;
    dataType: EMeasureDataType.Xml;
}

export interface SrImportRequest extends BaseImportRequest {
    repositoryKey: string;
    dataType?: EMeasureDataType.Sr;
}

export interface CpHttpSaveOptions {
    forcedReload: boolean;
    ignoreErrors?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class CpHttpService extends ServerRootService {
    constructor(
        private cacheService: CacheService,
        private httpClient: HttpClient,
    ) {
        super();
    }

    getData(idExaminations: string[], idCustomPageRoot?: string, skipLock?: boolean) {
        return this.httpClient.post<Json>(
            this.getServerRoot() + '/CustomPage/GetData',
            {
                idExaminations,
                idCustomPageRoot,
                skipLock,
            },
            this.buildOptions(),
        );
    }

    getDataset(idCustomPage: string) {
        const request = this.httpClient
            .post<IDatasetResponseDTO>(
                this.getServerRoot() + '/CustomPage/GetLayoutMetadata',
                {
                    id: idCustomPage,
                },
                this.buildOptions(),
            )
            .pipe(map((metadata) => metadata.jsonMetadata));

        return this.cacheService.get(`CpHttpService.getDataset.${idCustomPage}`, request);
    }

    getItemSourcesWithItemsByIdCustomPage(idCustomPage: string, ignoreTokenErrors: boolean = false): Observable<ICustomPageTypeItemsSourceDTO> {
        const request = this.httpClient.post<ICustomPageTypeItemsSourceDTO>(
            this.getServerRoot() + '/CustomPage/GetLayoutItemsSourceWithItemsByCustomPage',
            `"${idCustomPage}"`,
            this.buildOptions(null, ignoreTokenErrors),
        );

        return this.cacheService.get(`CpHttpService.getItemSourcesWithItemsByIdCustomPage.${idCustomPage}`, request);
    }

    getLayout(idCustomPage: string) {
        const request = this.httpClient.post<ICustomPageLayoutDTO>(
            this.getServerRoot() + '/CustomPage/GetLayout',
            `"${idCustomPage}"`,
            this.buildOptions(),
        );

        return this.cacheService.get(`CpHttpService.getLayout.${idCustomPage}`, request);
    }

    getLayoutList(idModelTypes: string[]) {
        const request = this.httpClient.post<ICustomPageLayoutHeader[]>(
            this.getServerRoot() + '/CustomPage/GetLayoutList',
            { idModelType: idModelTypes },
            this.buildOptions(),
        );

        return this.cacheService.get(`CpHttpService.getLayoutList.${idModelTypes.join('.')}`, request);
    }

    getLayoutListOnlyNotActive(idModelTypes: string[]) {
        const request = this.httpClient.post<ICustomPageLayoutHeader[]>(
            this.getServerRoot() + '/CustomPage/GetLayoutListOnlyNotActive',
            { idModelType: idModelTypes },
            this.buildOptions(),
        );

        return this.cacheService.get(`CpHttpService.getLayoutListOnlyNotActive.${idModelTypes.join('.')}`, request);
    }

    getPreloadableLayouts() {
        const request = this.httpClient.post<ICustomPageLayoutHeader[]>(
            this.getServerRoot() + '/CustomPage/GetLayoutList',
            { preloadSource: true },
            this.buildOptions(),
        );

        return this.cacheService.get(`CpHttpService.getPreloadableLayouts`, request);
    }

    getPlaceholdersByIdCustomPage(idCustomPage: string) {
        const request = this.httpClient.post<ICustomPageTypeDefaultValueDTO>(
            this.getServerRoot() + '/CustomPage/GetLayoutDefaultValue',
            `"${idCustomPage}"`,
            this.buildOptions(),
        );

        return this.cacheService.get(`CpHttpService.getPlaceholdersByIdCustomPage.${idCustomPage}`, request);
    }

    importData(data: any, idExaminations: string[]) {
        return this.httpClient.post<ICustomPageLayoutDTO>(
            this.getServerRoot() + '/CustomPage/ImportData',
            {
                data,
                idExaminations,
            },
            this.buildOptions(),
        );
    }

    importMeasuresFromXml(request: MeasureImportRequest) {
        return this.httpClient.post<Json>(
            this.getServerRoot() + '/Measure/ImportMeasures',
            request,
            this.buildOptions(),
        );
    }

    importSr(request: SrImportRequest) {
        return this.httpClient.post<Json>(
            this.getServerRoot() + '/Measure/ImportMeasures',
            request,
            this.buildOptions(),
        );
    }

    importMeasuresFromThirdPartsXml(report: Json, xml: string, type: XmlRequestType) {
        return this.httpClient.post<Json>(
            this.getServerRoot() + '/Measure/ImportDiaMeasures',
            {
                report,
                xml,
                type,
            },
            this.buildOptions(),
        );
    }

    resolveIdCustomPageRoot(idCustomPageRootList: string[], idExaminations: string[]) {
        return this.httpClient
            .post<ICustomPageLayoutMultiReportDTO | null>(
                this.getServerRoot() + '/CustomPage/GetLayoutMultiReport',
                { idCustomPageRootList, idExaminations },
                this.buildOptions(),
            )
            .pipe(map((data) => (data ? data.id : null)));
    }

    save(data: any, options: CpHttpSaveOptions) {
        return this.httpClient.post<Json>(
            this.getServerRoot() + '/CustomPage/SetData',
            { data: data.Report, forcedReload: options.forcedReload },
            this.buildOptions(undefined, options.ignoreErrors),
        );
    }

    // #region Http methods

    retrievePatientHistory$(request: PatientHistoryRequest): Observable<IPatientHistory[]> {
        return this.httpClient.post<IPatientHistory[]>(
            this.getServerRoot() + '/CustomPageImport/RetrievePatientHistory',
            request,
            this.buildOptions(),
        );
    }

    retrieveImportReportList$(request: ImportReportListRequest): Observable<IPatientHistory[]> {
        return this.httpClient.post<IPatientHistory[]>(
            this.getServerRoot() + '/CustomPageImport/RetrieveImportReportList',
            {
                ...request,
                bindings: [],
            },
            this.buildOptions(),
        );
    }

    saveClinicalImages(data: ClinicalRepositoryData[]) {
        return this.httpClient.post<unknown>(
            this.getServerRoot() + '/ClinicalRepository/SaveClinicalImages',
            data,
            this.buildOptions(),
        );
    }

    deleteClinicalImages(images: string[]) {
        return this.httpClient.post<unknown>(
            this.getServerRoot() + '/ClinicalRepository/DeleteClinicalImages',
            images,
            this.buildOptions(),
        );
    }

    getEcgTrace$(examinationId: string, lowFilter?: number, highFilter?: number, reportId?: string) {
        let params = '?examinationId=' + examinationId;
        params += lowFilter != null ? '&lowFilter=' + lowFilter : '';
        params += highFilter != null ? '&highFilter=' + highFilter : '';
        params += reportId != null ? '&idClinicalReport=' + reportId : '';
        return this.httpClient.get(this.getServerRoot() + '/ECG/GetEcgData' + params, this.buildOptions());
    }

    getPreviousEcgs(ecgHistorySearchParam: ecgHistorySearchParam) {
        return this.httpClient.post(
            this.getServerRoot() + '/ECG/RetrievePreviousEcgs',
            ecgHistorySearchParam,
            this.buildOptions(),
        );
    }

    // #endregion
}
